/**
 * PATH: apps/qa/src/components/camera-capture/PreviewControls.tsx
 * PURPOSE: Post-capture controls — notes, voice memo, save/retake/annotate actions
 */

import {
  Mic, MicOff, Check, RotateCcw, Loader2,
  Clock, AlertTriangle, Upload, Pencil, Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface PreviewControlsProps {
  notes: string
  onNotesChange: (v: string) => void
  audio: {
    isRecording: boolean
    audioBlob: Blob | null
    duration: number
    startRecording: () => void
    stopRecording: () => void
    clearRecording: () => void
  }
  isOnline: boolean
  isUploading: boolean
  onRetake: () => void
  onAnnotate: () => void
  onSave: () => void
}

export function PreviewControls({
  notes, onNotesChange, audio, isOnline, isUploading,
  onRetake, onAnnotate, onSave,
}: PreviewControlsProps) {
  return (
    <div className="space-y-3">
      {/* Notes input */}
      <input
        type="text"
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        placeholder="Add a note (optional)..."
        className="w-full px-4 py-2.5 rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/50 outline-none"
      />

      {/* Voice note */}
      <div className="flex items-center gap-2">
        <button
          onClick={audio.isRecording ? audio.stopRecording : audio.startRecording}
          className={cn(
            'px-4 py-2 rounded-xl flex items-center gap-2 transition-colors',
            audio.isRecording
              ? 'bg-red-500 text-white animate-pulse'
              : audio.audioBlob
              ? 'bg-emerald-600 text-white'
              : 'bg-white/10 text-white hover:bg-white/20'
          )}
        >
          {audio.isRecording ? (
            <>
              <MicOff className="w-4 h-4" />
              Stop ({audio.duration}s)
            </>
          ) : audio.audioBlob ? (
            <>
              <Check className="w-4 h-4" />
              Voice recorded
            </>
          ) : (
            <>
              <Mic className="w-4 h-4" />
              Voice note
            </>
          )}
        </button>
        {audio.audioBlob && (
          <button
            onClick={audio.clearRecording}
            className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={onRetake}
          className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
        >
          <RotateCcw className="w-5 h-5" />
          Retake
        </button>
        <button
          onClick={onAnnotate}
          className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <Pencil className="w-5 h-5" />
          Annotate
        </button>
        <button
          onClick={onSave}
          disabled={isUploading}
          className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Uploading...
            </>
          ) : isOnline ? (
            <>
              <Upload className="w-5 h-5" />
              Save
            </>
          ) : (
            <>
              <Clock className="w-5 h-5" />
              Queue
            </>
          )}
        </button>
      </div>

      {!isOnline && (
        <p className="text-center text-xs text-amber-400 flex items-center justify-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Offline - will upload when connected
        </p>
      )}
    </div>
  )
}
